import { useEffect, useMemo, useState } from 'react'
import { Group } from '@common/ui'
import { com } from '@eidu/web'
import FieldSingleSelect from './FieldSingleSelect'
import { entityTypeIdByFieldIdOrFirst, fieldsOfEntityType, findFieldByIdOrFirst } from '../../util/EntityTypeExtensions'
import useEntityType from '../../io/useEntityType'
import EntityTypeField from '../../pages/permission/path/EntityTypeField'
import EntitySelector = com.eidu.entity.selector.EntitySelector
import FieldId = com.eidu.entity.field.FieldId
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import EntityTypeId = com.eidu.entity.type.EntityTypeId

type FieldValueEqualsInputProps = {
  entityTypeId: EntityTypeId
  allEntityTypes: readonly EntityType[]
  selector: EntitySelector.FieldValueEqualsUserFieldValue | undefined
  setSelector: (selector: EntitySelector.FieldValueEqualsUserFieldValue) => void
  disabled: boolean
}

type WithUserEntityTypeProps = {
  localEntityType: EntityType
  localUserEntityTypeId: EntityTypeId
  setLocalUserEntityTypeId: (id: EntityTypeId) => void
  userEntityTypes: readonly EntityType[]
  localFieldId: FieldId | undefined
  setLocalFieldId: (id: FieldId) => void
  localUserFieldId: FieldId | undefined
  setLocalUserFieldId: (id: FieldId) => void
  setSelector: (selector: EntitySelector.FieldValueEqualsUserFieldValue) => void
  disabled: boolean
}

const WithUserEntityType = ({
  localEntityType,
  localUserEntityTypeId,
  setLocalUserEntityTypeId,
  userEntityTypes,
  localFieldId,
  setLocalFieldId,
  localUserFieldId,
  setLocalUserFieldId,
  setSelector,
  disabled,
}: WithUserEntityTypeProps) => {
  const { entityType: localUserEntityType } = useEntityType(localUserEntityTypeId)
  const field = useMemo(() => findFieldByIdOrFirst(localEntityType, localFieldId), [localEntityType, localFieldId])
  const userField = useMemo(
    () => findFieldByIdOrFirst(localUserEntityType ?? undefined, localUserFieldId),
    [localUserEntityType, localUserFieldId]
  )
  useEffect(() => {
    if (field && userField) {
      setSelector(new EntitySelector.FieldValueEqualsUserFieldValue(field.id, userField.id))
    }
  }, [localFieldId, localUserFieldId])

  if (!localUserEntityType) return null
  return (
    <Group gap="md" align="flex-start" wrap="nowrap">
      <FieldSingleSelect
        label="Field"
        options={fieldsOfEntityType(localEntityType)}
        selected={field}
        entityType={localEntityType}
        setSelected={(newField) => setLocalFieldId(newField.id)}
        disabled={disabled}
      />
      <EntityTypeField
        label="User type"
        types={userEntityTypes}
        onChange={(newType) => setLocalUserEntityTypeId(newType.id)}
        entityTypeId={localUserEntityTypeId}
        disabled={disabled}
      />
      <FieldSingleSelect
        label="User field"
        options={fieldsOfEntityType(localUserEntityType)}
        entityType={localUserEntityType}
        selected={userField}
        setSelected={(newField) => setLocalUserFieldId(newField.id)}
        disabled={disabled}
      />
    </Group>
  )
}

const FieldValueEqualsUserFieldValueInput = ({
  entityTypeId,
  allEntityTypes,
  selector,
  setSelector,
  disabled,
}: FieldValueEqualsInputProps) => {
  const userEntityTypes = allEntityTypes.filter((entityType) => entityType.kind.equals(EntityTypeKind.Companion.User))
  const { entityType: localEntityType } = useEntityType(entityTypeId)

  const [localFieldId, setLocalFieldId] = useState<FieldId | undefined>(selector?.fieldId)
  const [localUserFieldId, setLocalUserFieldId] = useState<FieldId | undefined>(selector?.userFieldId)
  const [localUserEntityTypeId, setLocalUserEntityTypeId] = useState<EntityTypeId | undefined>(
    entityTypeIdByFieldIdOrFirst(userEntityTypes, localUserFieldId)
  )

  if (!localEntityType || !localUserEntityTypeId) return null
  return (
    <WithUserEntityType
      localEntityType={localEntityType}
      localUserEntityTypeId={localUserEntityTypeId}
      setLocalUserEntityTypeId={setLocalUserEntityTypeId}
      userEntityTypes={userEntityTypes}
      localFieldId={localFieldId}
      setLocalFieldId={setLocalFieldId}
      localUserFieldId={localUserFieldId}
      setLocalUserFieldId={setLocalUserFieldId}
      setSelector={setSelector}
      disabled={disabled}
    />
  )
}

export default FieldValueEqualsUserFieldValueInput
